import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { User } from '@tabservice/tabfile-client';
import { UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, SectionHeader } from '@/components/ui/card';
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ui/confirm';
import { Field, Input } from '@/components/ui/field';
import { Combobox } from '@/components/ui/combobox';
import { EmptyState, Spinner, TableSkeleton } from '@/components/ui/feedback';
import { Pill } from '@/components/ui/pill';
import { SortableTH, Table, TableWrap, TBody, TD, TH, THead, TR, useServerSort, TablePagination} from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { queryKeys, usersPageQuery } from '@/lib/queries';
import { tabfile, errorMessage } from '@/lib/api';
import { formatRelative } from '@/lib/format';

/** Rows per page, matching the portal's tables. */
const PAGE_SIZE = 10;

const roleLabel: Record<string, string> = {
  owner: 'Owner',
  member: 'Member',
  staff: 'Tab Service staff',
};

export function UsersTab({ isOwner }: { isOwner: boolean }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [page, setPage] = useState(1);
  // The column headers order the whole list, not the page on screen.
  const { sort, onSort, param: sortParam } = useServerSort(null, () => setPage(1));
  const users = useQuery(usersPageQuery(page, PAGE_SIZE, sortParam));
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [removing, setRemoving] = useState<User | null>(null);

  const invite = useMutation({
    mutationFn: () => tabfile.POST('/v1/users', { body: { email: email.trim(), role: role as 'owner' | 'member' } }),
    onSuccess: () => {
      toast.success('Invite sent', `${email.trim()} can now set a password and sign in.`);
      setEmail('');
      setInviteOpen(false);
      void queryClient.invalidateQueries({ queryKey: queryKeys.users });
    },
    onError: (error) => toast.error('Could not send the invite', errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (userId: string) => tabfile.DELETE('/v1/users/{userId}', { params: { path: { userId } } }),
    onSuccess: () => {
      toast.success('User removed', 'They can no longer sign in to this account.');
      setRemoving(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.users });
    },
    onError: (error) => toast.error('Could not remove the user', errorMessage(error)),
  });

  const items = users.data?.items ?? [];


  return (
    <Card data-tour="users">
      <SectionHeader
        title="Users"
        description={
          isOwner
            ? 'Owners can invite and remove people. Members can file and read everything.'
            : 'Only an owner can invite or remove people on this account.'
        }
        actions={
          isOwner ? (
            <Button onClick={() => setInviteOpen(true)}>
              <UserPlus className="h-4 w-4" aria-hidden="true" />
              Invite someone
            </Button>
          ) : null
        }
      />

      {users.isPending ? (
        <TableSkeleton rows={3} columns={4} />
      ) : items.length === 0 ? (
        <EmptyState
          title="No users yet"
          description="Invite the people who need to file."
          action={isOwner ? <Button onClick={() => setInviteOpen(true)}>Invite someone</Button> : undefined}
        />
      ) : (
        <>
          <TableWrap>
            <Table>
              <THead>
                <tr>
                  <SortableTH columnKey="email" sort={sort} onSort={onSort}>
                    Email
                  </SortableTH>
                  <SortableTH columnKey="role" sort={sort} onSort={onSort}>
                    Role
                  </SortableTH>
                  <SortableTH columnKey="status" sort={sort} onSort={onSort}>
                    Status
                  </SortableTH>
                  <SortableTH columnKey="lastSignIn" sort={sort} onSort={onSort}>
                    Last signed in
                  </SortableTH>
                  <TH aria-label="Actions" />
                </tr>
              </THead>
              <TBody>
                {items.map((item) => (
                  <TR key={item.id}>
                    <TD className="font-medium">{item.email}</TD>
                    <TD className="text-muted-foreground">{roleLabel[item.role] ?? item.role}</TD>
                    <TD>
                      <Pill tone={item.status === 'active' ? 'success' : item.status === 'invited' ? 'info' : 'neutral'}>
                        {item.status === 'active' ? 'Active' : item.status === 'invited' ? 'Invited' : 'Disabled'}
                      </Pill>
                    </TD>
                    <TD className="text-muted-foreground">
                      {item.lastSignInAt ? formatRelative(item.lastSignInAt) : 'never'}
                    </TD>
                    <TD className="text-right">
                      {isOwner && item.role !== 'owner' ? (
                        <Button variant="destructive" size="sm" onClick={() => setRemoving(item)}>
                          Remove
                        </Button>
                      ) : null}
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableWrap>
          <TablePagination
            page={page}
            pageSize={PAGE_SIZE}
            total={users.data?.total ?? users.data?.items?.length ?? 0}
            busy={users.isFetching}
            onPageChange={setPage}
          />
        </>
      )}

      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent
          title="Invite a user"
          description="They receive an email to set a password."
          footer={
            <>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button
                variant="primary"
                disabled={!/.+@.+\..+/.test(email.trim()) || invite.isPending}
                onClick={() => invite.mutate()}
              >
                {invite.isPending ? <Spinner /> : null}
                Send invite
              </Button>
            </>
          }
        >
          <div className="flex flex-col gap-4">
            <Field label="Email">
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  value={email}
                  autoFocus
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="name@company.com"
                />
              )}
            </Field>
            <Field label="Role" hint="Owners can manage keys and people.">
              {(props) => (
                <Combobox
                  id={props.id}
                  value={role}
                  onValueChange={setRole}
                  options={[
                    { value: 'member', label: 'Member' },
                    { value: 'owner', label: 'Owner' },
                  ]}
                />
              )}
            </Field>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null);
        }}
        title="Remove this user"
        description={removing?.email}
        body={
          <p className="text-base text-muted-foreground">
            They lose access immediately. E-files they made are unaffected.
          </p>
        }
        confirmLabel="Remove user"
        destructive
        pending={remove.isPending}
        onConfirm={() => {
          if (removing) remove.mutate(removing.id);
        }}
      />
    </Card>
  );
}
